<!-- The guided tour on screen (src/stores/tour.ts, steps in src/features/tour.ts): #tourLayer (z-[97]) with a dimming layer
     that swallows clicks, the highlight frame #tourSpot and the bubble #tourTip (classes .tour-* in legacy.css). The store
     places the frame and the bubble (inline styles); here: the bubble's content, drawn again at every step with its
     entrance animation, and the keyboard (capture phase, registered once the shell is mounted: after the legacy
     listeners of the @ menu, before any app dialog) and window resizing. -->
<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from '../composables/useI18n'
import { useTourStore } from '../stores/tour'
import { TOUR_STEPS } from '../features/tour'

const { t } = useI18n()
const tour = useTourStore()
const tip = ref<HTMLElement | null>(null)

// Every step shown (even the same one again): the entrance animation starts again
watch(() => tour.seq, () => {
  const el = tip.value
  if (!el) return
  el.classList.remove('tour-tip-in'); void el.offsetWidth; el.classList.add('tour-tip-in')
}, { flush: 'post' })

const onKey = (e: KeyboardEvent) => tour.keydown(e)
const onResize = () => { if (tour.active) tour.place() }
onMounted(() => {
  document.addEventListener('keydown', onKey, true)
  window.addEventListener('resize', onResize)
})
onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKey, true)
  window.removeEventListener('resize', onResize)
})
</script>

<template>
  <Teleport to="body">
    <div v-if="tour.started" id="tourLayer" :class="['fixed inset-0 z-[97]', { hidden: !tour.active }]">
      <div class="absolute inset-0" @click.stop></div>
      <div id="tourSpot" class="tour-spot"></div>
      <div id="tourTip" ref="tip" role="dialog" aria-live="polite" class="tour-tip absolute bg-darkcard border border-gray-800 rounded-2xl shadow-2xl p-4 md:p-5">
        <template v-for="n in [tour.seq]" :key="n">
          <div class="flex items-start gap-3">
            <span class="w-10 h-10 rounded-xl bg-brand/15 text-brand flex items-center justify-center flex-shrink-0"><i :class="['fas', tour.step.icon]"></i></span>
            <div class="min-w-0 flex-1"><div class="text-[11px] font-bold text-gray-500">{{ t('{i} of {total}', { i: tour.i + 1, total: tour.total }) }}</div>
              <h3 class="font-black text-gray-100 text-base md:text-lg leading-tight">{{ tour.step.title() }}</h3></div>
            <button type="button" :title="t('Close the tour')" class="text-gray-500 hover:text-brand -mt-1" @click="tour.end()"><i class="fas fa-xmark"></i></button>
          </div>
          <!-- The step texts are the app's own translations (with <b>), never user data -->
          <p class="text-sm text-gray-300 mt-3 leading-relaxed" v-html="tour.step.text(tour.phone)"></p>
          <div class="flex items-center gap-1.5 mt-4"><span v-for="(_, k) in TOUR_STEPS" :key="k" :class="['h-1.5 rounded-full transition-all', k === tour.i ? 'w-5 bg-brand' : 'w-1.5 bg-gray-800']"></span></div>
          <div class="flex items-center gap-2 mt-4">
            <button v-if="tour.i !== tour.total - 1" type="button" class="text-xs font-semibold text-gray-500 hover:text-gray-300 mr-auto" @click="tour.end()">{{ t('Skip the tour') }}</button>
            <button v-if="tour.i" type="button" :class="[tour.i === tour.total - 1 ? 'mr-auto' : '', 'px-4 py-2 rounded-xl bg-darkbtn text-gray-300 text-sm font-bold hover:text-gray-100']" @click="tour.go(tour.i - 1)"><i class="fas fa-arrow-left mr-1"></i>{{ t('Back') }}</button>
            <button v-if="tour.i === tour.total - 1" type="button" class="px-4 py-2 rounded-xl bg-brand hover:bg-brandDark text-white text-sm font-bold" @click="tour.end(true)">{{ t("Let's start!") }}</button>
            <button v-else type="button" class="px-4 py-2 rounded-xl bg-brand hover:bg-brandDark text-white text-sm font-bold" @click="tour.go(tour.i + 1)">{{ tour.i ? t('Next') : t('Start the tour') }} <i class="fas fa-arrow-right ml-1"></i></button>
          </div>
        </template>
      </div>
    </div>
  </Teleport>
</template>
